import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import config from "@/config.js";
import t from "@/services/translationService.js";
import showcaseController from "@modules/showcase/showcaseController.js";
import { viewLink, editButton } from "./rowActions.js";

/**
 * Cockpit → Gear Manager. Lists gear items in their display order, with
 * drag-to-reorder, view, edit and add. This replaces the Gear page's old
 * view/edit-mode toggle: the public gear pile is now view-only and the pile's
 * scattered layout no longer has to double as a reorder grid.
 *
 * props: { lang }
 */
export default class GearManagerPanel extends CockpitPanel {
  constructor(props) {
    super(props);
    // `error` means the list could not be loaded at all; `saveError` is a
    // banner above a list that is still perfectly usable (a failed reorder).
    this.state = { items: [], order: [], error: "", saveError: "" };
    this.draggedId = null;
  }

  async onBeforeMount() {
    await this.load();
  }

  async load() {
    try {
      await showcaseController.ensureLoaded();
      this.state.items = showcaseController.manager.all();
      this.state.order = this.state.items.map((item) => item.gearItemId);
      this.state.error = "";
      this.state.saveError = "";
    } catch (err) {
      this.state.items = [];
      this.state.order = [];
      this.state.error = err.message || t.t("common.error");
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.gear"));
  }

  lead() {
    return Component.escape(t.t("cockpit.gear.lead"));
  }

  actions() {
    return `<button type="button" class="btn-primary" data-action="add">+ ${Component.escape(t.t("cockpit.gear.add"))}</button>`;
  }

  #byId(id) {
    return this.state.items.find((item) => item.gearItemId === id);
  }

  #orderedItems() {
    return this.state.order.map((id) => this.#byId(id)).filter(Boolean);
  }

  #visibility(item) {
    if (item.isPublic()) return { key: "public", label: t.t("cockpit.visibility.public") };
    if (item.isDraft()) return { key: "draft", label: t.t("cockpit.visibility.draft") };
    return { key: "private", label: t.t("cockpit.visibility.private") };
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);

    const items = this.#orderedItems();
    if (!items.length) return CockpitPanel.empty(t.t("showcase.empty"));

    const { lang } = this.props;
    const rows = items
      .map((item, index) => {
        const thumb = item.image
          ? `<img class="cockpit-row__thumb" src="${config.imageBase}/showcase/${encodeURIComponent(item.gearItemId)}/${encodeURIComponent(item.image)}?size=thumb" alt="" loading="lazy" />`
          : `<span class="cockpit-row__thumb cockpit-row__thumb--empty"></span>`;
        const visibility = this.#visibility(item);
        return `
          <tr class="cockpit-sortable__row" draggable="true" data-id="${Component.escape(item.gearItemId)}">
            <td class="cockpit-sortable__handle" aria-hidden="true">⠿</td>
            <td class="cockpit-row__nowrap"><span class="cockpit-row__index">${index + 1}</span></td>
            <td>${thumb}</td>
            <td><span class="cockpit-row__name">${Component.escape(item.title(lang))}</span></td>
            <td><span class="state-badge state-badge--${visibility.key}">${Component.escape(visibility.label)}</span></td>
            <td class="cockpit-table__actions">
              ${viewLink(`#/gear/${encodeURIComponent(item.gearItemId)}`)}
              ${editButton(item.gearItemId)}
            </td>
          </tr>`;
      })
      .join("");

    return `
      ${this.state.saveError ? CockpitPanel.error(this.state.saveError) : ""}
      <p class="cockpit-hint">${Component.escape(t.t("cockpit.gear.reorderHint"))}</p>
      <div class="cockpit-table__wrap">
        <table class="cockpit-table cockpit-sortable">
          <thead>
            <tr>
              <th class="cockpit-table__handle-col"></th>
              <th class="cockpit-table__index-col">#</th>
              <th class="cockpit-table__thumb-col"></th>
              <th>${Component.escape(t.t("cockpit.gear.colTitle"))}</th>
              <th>${Component.escape(t.t("cockpit.gear.colVisibility"))}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  onMounted() {
    const add = this.$('[data-action="add"]');
    if (add) this.listen(add, "click", () => this.#edit(null));

    this.$$("[data-edit]").forEach((btn) =>
      this.listen(btn, "click", () => this.#edit(this.#byId(btn.getAttribute("data-edit"))))
    );

    this.$$(".cockpit-sortable__row").forEach((row) => {
      const id = row.getAttribute("data-id");

      this.listen(row, "dragstart", (e) => {
        this.draggedId = id;
        row.classList.add("cockpit-sortable__row--dragging");
        e.dataTransfer.effectAllowed = "move";
      });

      this.listen(row, "dragend", () => {
        row.classList.remove("cockpit-sortable__row--dragging");
        this.draggedId = null;
        this.#persistOrder();
      });

      this.listen(row, "dragover", (e) => {
        e.preventDefault();
        if (!this.draggedId || this.draggedId === id) return;
        const order = [...this.state.order];
        const from = order.indexOf(this.draggedId);
        const to = order.indexOf(id);
        if (from === -1 || to === -1) return;
        order.splice(from, 1);
        order.splice(to, 0, this.draggedId);
        // Mutate state and patch the DOM directly — a setState() re-render
        // would replace the rows mid-gesture and cancel the drag.
        this.state.order = order;
        this.#reflectOrder();
      });
    });
  }

  /** Move the rows in the live DOM to match state.order and renumber them. */
  #reflectOrder() {
    const body = this.$(".cockpit-sortable tbody");
    if (!body) return;
    this.state.order.forEach((id, index) => {
      const row = body.querySelector(`[data-id="${CSS.escape(id)}"]`);
      if (!row) return;
      body.appendChild(row);
      const cell = row.querySelector(".cockpit-row__index");
      if (cell) cell.textContent = String(index + 1);
    });
  }

  async #persistOrder() {
    try {
      await showcaseController.reorder(this.state.order);
      if (this.state.saveError) this.setState({ saveError: "" });
    } catch (err) {
      this.setState({ saveError: err.message || t.t("common.error") });
    }
  }

  #edit(item) {
    showcaseController.openEditor(item, () => this.reload());
  }

  async reload() {
    showcaseController.invalidate();
    await this.load();
    this.setState({});
  }
}
